import { tr } from "./i18n.js";
/**
 * Pure helpers for the Analytics page (no React, no DOM) so they can be
 * unit-tested with node:
 *
 *     node --test frontend/scripts/analyticsFormat.test.mjs
 *
 * Scores and indexes come from `backend/analytics_engine.py`:
 * 1.0 = the property's typical story for the range, 2.0 = double.
 */

export const RANGES = [30, 90, 180, 365];

export const METRIC_KEYS = ["web", "fb", "eng"];
export const METRIC_SHORT = { get web() { return tr("Web"); }, get fb() { return tr("FB views"); }, get eng() { return tr("Engagement"); } };
export const METRIC_LONG = {
  get web() { return tr("Website views in the first 7 days"); },
  get fb() { return tr("Facebook views"); },
  get eng() { return tr("Social engagement (reactions, comments, shares, Instagram likes)"); },
};

export const TIERS = {
  strong: { get label() { return tr("Strong"); }, cls: "an-tier-strong" },
  above: { get label() { return tr("Above typical"); }, cls: "an-tier-above" },
  below: { get label() { return tr("Below typical"); }, cls: "an-tier-below" },
  weak: { get label() { return tr("Weak"); }, cls: "an-tier-weak" },
  maturing: { get label() { return tr("Maturing"); }, cls: "an-tier-neutral" },
  syncing: { get label() { return tr("Waiting for numbers"); }, cls: "an-tier-neutral" },
};

export const CONFIDENCE = {
  solid: { get label() { return tr("Solid"); }, get title() { return tr("10 or more stories"); } },
  early: { get label() { return tr("Early signal"); }, get title() { return tr("5 to 9 stories — treat as a hint"); } },
  thin: { get label() { return tr("Few stories"); }, get title() { return tr("3 or 4 stories — too few to act on"); } },
};

export function tierForScore(score) {
  if (score === null || score === undefined) return "maturing";
  if (score >= 1.5) return "strong";
  if (score >= 1.0) return "above";
  if (score >= 0.67) return "below";
  return "weak";
}

export function tierMeta(tier) {
  return TIERS[tier] || TIERS.maturing;
}

/** 1.83 → "1.8×"; null → "—". */
export function formatIndex(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (v >= 10) return `${Math.round(v)}×`;
  return `${(Math.round(v * 10) / 10).toFixed(1)}×`;
}

/** 12345 → "12.3k"; null → "—". */
export function formatCount(v) {
  if (v === null || v === undefined) return "—";
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 10000) return `${Math.round(v / 1000)}k`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
}

/** 0.234 → "23%"; null → "—". */
export function formatPct(v) {
  if (v === null || v === undefined) return "—";
  return `${Math.round(v * 100)}%`;
}

/** Width (0–100) of an index bar, on a log scale centred at 1.0 = 50%. */
export function indexBarWidth(v) {
  if (v === null || v === undefined || v <= 0) return 0;
  const clamped = Math.min(4, Math.max(0.25, v));
  return Math.round(((Math.log2(clamped) + 2) / 4) * 100);
}

export function engagementOf(a) {
  return (a?.fb_engagement || 0) + (a?.ig_engagement || 0);
}

export const SORTS = {
  score: { get label() { return tr("Score"); }, get: (a) => a.score },
  date: { get label() { return tr("Newest"); }, get: (a) => a.published_at },
  web: { get label() { return tr("Web views"); }, get: (a) => (a.web_d7 ?? a.web_so_far) },
  fb: { get label() { return tr("FB views"); }, get: (a) => (a.fb_views ?? a.fb_views_so_far) },
  eng: { get label() { return tr("Engagement"); }, get: (a) => (a.mature ? engagementOf(a) : a.eng_so_far) },
};

/** Descending sort; missing values always last. Returns a new array. */
export function sortArticles(list, key) {
  const get = (SORTS[key] || SORTS.score).get;
  return [...(list || [])].sort((x, y) => {
    const a = get(x);
    const b = get(y);
    const an = a === null || a === undefined;
    const bn = b === null || b === undefined;
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    if (a < b) return 1;
    if (a > b) return -1;
    return 0;
  });
}

/** Case-insensitive headline search plus a status filter. */
export function filterArticles(list, { query = "", status = "all" } = {}) {
  const q = query.trim().toLowerCase();
  return (list || []).filter((a) => {
    if (status === "scored" && !a.mature) return false;
    if (status === "pending" && a.mature) return false;
    if (q && !(a.headline || "").toLowerCase().includes(q)) return false;
    return true;
  });
}

/** Articles for a clicked group, in the order the backend ranked them. */
export function groupArticles(group, byId) {
  return (group?.article_ids || []).map((id) => byId[id]).filter(Boolean);
}

export const HEAT_DAYPARTS = [
  "Early (5–9)", "Morning (9–12)", "Afternoon (12–5)", "Evening (5–9)", "Night (9–5)",
];
export const HEAT_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Heatmap cells → {`${weekday}|${daypart}`: cell}. */
export function heatIndex(cells) {
  const out = {};
  for (const c of cells || []) out[`${c.weekday}|${c.daypart}`] = c;
  return out;
}

/** Share of the range's stories that has final numbers. */
export function coverageLine(cov) {
  if (!cov || !cov.articles) return tr("No published stories in the last year yet.");
  const parts = [
    tr("{web_final} of {articles} with website numbers", { web_final: cov.web_final, articles: cov.articles }),
    tr("{social_final} with Facebook numbers", { social_final: cov.social_final }),
    `${cov.tagged} tagged`,
  ];
  return parts.join(" · ");
}
